// Dynamic (hourly) retail tariff, behind the meter only.
//
// Every hour's energy is billed at that hour's day-ahead market price plus the
// supplier fee; the regulated charges are added per kWh. The day-ahead prices
// are the study's own market data for the reference year (backend
// Configuration/dynamic_tariff.py). With this scheme the monthly peak /
// off-peak table and the peak hours are not used.

import { isBtmSettlement } from "./btm";

export const DYNAMIC_SCHEME = "Dynamic";

export const DYNAMIC_SCHEME_LABEL = "Dynamic (hourly)";

export const DYNAMIC_TITLE = "Dynamic tariff — the price changes every hour";

export const DYNAMIC_EXPLAINER_TEXT =
  "Every hour's energy is billed at that hour's day-ahead market price plus the supplier fee, and " +
  "the regulated charges are added per kWh. The day-ahead prices are the study's own market data " +
  "for the reference year, the same prices used for every market flow of the study. The monthly " +
  "table and the peak hours are not used. A dynamic contract needs a meter that records hourly " +
  "consumption.";

export const SUPPLIER_FEE_LABEL = "Supplier fee on the energy (EUR/kWh)";

export const SUPPLIER_FEE_HELPER_TEXT =
  "The supplier's charge per kWh on top of the day-ahead price, from the dynamic contract. " +
  "Without VAT, like the day-ahead price.";

export const REGULATED_CHARGES_LABEL = "Regulated charges (EUR/kWh)";

export const REGULATED_CHARGES_HELPER_TEXT =
  "Network charges, public-service charges and the other regulated charges per kWh, from the " +
  "bill, without VAT. They are paid on every kWh drawn from the grid, whatever the hour.";

export const NEGATIVE_PRICES_LABEL = "Negative day-ahead prices are passed to the bill";

export const NEGATIVE_PRICES_HELPER_TEXT =
  "Off: an hour with a negative day-ahead price is billed at zero plus the supplier fee. On: the " +
  "bill follows the price below zero, so a battery charging from the grid in those hours is paid " +
  "for it. Check which the contract says.";

export const DYNAMIC_NOT_BTM_TEXT =
  "The dynamic tariff applies only to a Behind-the-Meter study; this revenue model uses the " +
  "monthly table.";

export const DYNAMIC_NO_INPUTS_TEXT =
  "Enter the supplier fee and the regulated charges: without them every hour is priced at the " +
  "bare day-ahead price.";

export const DYNAMIC_INPUT_INVALID_TEXT = "Enter a number of 0 or more (EUR/kWh).";

// BTM section (retail price over the years).
export const ESCALATION_NOT_USED_DYNAMIC_TEXT =
  "Not used with a dynamic tariff: the energy part follows the study's day-ahead prices, and the " +
  "regulated charges rise at the escalation of the regulated charges below.";

export const RETAIL_DYNAMIC_ON_TEXT =
  "Dynamic tariff (Technical page): the energy part follows the study's day-ahead prices — on " +
  "the forward price path when one is set on the Financial Assumptions page, otherwise held at the " +
  "reference year, like the study's market revenues. Its share of the saving comes from the " +
  "dispatch itself. The regulated charges rise at the escalation of the regulated charges.";

export const TOU_DYNAMIC_HELPER_TEXT =
  "With the dynamic tariff the battery may charge from the grid in the cheap hours of the hourly " +
  "price and discharge into the load in the dear ones; the cost of that energy is charged in full " +
  "against the saving.";

export function isDynamicScheme(billingScheme) {
  return String(billingScheme || "").trim().toLowerCase().startsWith("dynamic");
}

const isBlank = (v) => v === "" || v === null || v === undefined;

// The run's dynamic-tariff state: on only for a BTM study on the Dynamic scheme.
export function dynamicTariffState(cfg) {
  const tariffs = cfg?.feasibility?.technical?.tariffs || {};
  const selected = isDynamicScheme(tariffs.billingScheme);
  const dynamic = selected && isBtmSettlement(cfg?.settlementType);
  const block = tariffs.dynamic || {};
  return {
    selected,
    dynamic,
    selectedOutsideBtm: selected && !isBtmSettlement(cfg?.settlementType),
    missingInputs: dynamic && isBlank(block.supplierFeeEurKwh) && isBlank(block.regulatedEurKwh),
  };
}

// Field errors for the two prices (blank is allowed and means 0).
export function validateDynamicTariff(tariffs) {
  const block = tariffs?.dynamic || {};
  const errors = {};
  for (const key of ["supplierFeeEurKwh", "regulatedEurKwh"]) {
    const raw = block[key];
    if (isBlank(raw)) continue;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) errors[key] = DYNAMIC_INPUT_INVALID_TEXT;
  }
  return errors;
}
